import React from 'react'

import cn from 'classnames'

import { ConfigLoadingStatus } from '@src/enums'
import { useFetchAllTokens } from '@src/hooks/useFetchAllTokens'
import { useConfigStore } from '@src/zustand_stores'

interface ChainSelectorProps {
    chains: Record<string, any>
    selectedChain: number | string
    onChainClicked: (chainId: number) => void
    disabledChains?: number[]
    highlightedChain?: number
    tokenQuery: string
    activeChainSelection?: number
    isMobile: boolean
}

export const ChainSelector: React.FC<ChainSelectorProps> = ({
    chains,
    selectedChain,
    onChainClicked,
    disabledChains,
    highlightedChain,
    tokenQuery,
    activeChainSelection,
    isMobile,
}) => {
    const configLoading = useConfigStore(config => config.loading)
    const { isLoading: zustandTokensLoading } = useFetchAllTokens()

    const isConfigurationLoading = React.useMemo(
        () =>
            configLoading === ConfigLoadingStatus.loading_not_yet_started ||
            configLoading === ConfigLoadingStatus.loading ||
            zustandTokensLoading,
        [configLoading, zustandTokensLoading]
    )
    return (
        <>
            {isConfigurationLoading ? (
                // Show chain placeholders when configuration is loading
                <>
                    {Array.from({ length: isMobile ? 10 : 8 }).map((_, index) => (
                        <div
                            key={`chain-placeholder-${index}`}
                            className={cn(
                                'animate-pulse',
                                isMobile
                                    ? 'flex flex-col items-center justify-center gap-0.5 rounded-[10px] px-1 py-1.5'
                                    : 'flex w-full items-center gap-2 rounded-[10px] px-3 py-2'
                            )}
                        >
                            {/* Chain icon placeholder */}
                            <div
                                className={
                                    isMobile
                                        ? 'h-6 w-6 rounded-full bg-neutral-700'
                                        : 'm-0.5 h-7 w-7 rounded-full bg-neutral-700'
                                }
                            />
                            {/* Chain name placeholder */}
                            <div
                                className={cn(
                                    'rounded bg-neutral-700',
                                    isMobile ? 'h-3 w-12 text-[10px]' : 'h-4 w-20 text-[14px]'
                                )}
                            />
                        </div>
                    ))}
                </>
            ) : (
                // Show actual chains when loaded
                Object.entries(chains)
                    .sort(([networkIdA], [networkIdB]) => {
                        if (+networkIdA === +selectedChain) return -1
                        if (+networkIdB === +selectedChain) return 1
                        return 0
                    })
                    .map(([networkId, chain]) => {
                        // Determine if this chain is temporarily selected
                        const isTemporarilySelected = activeChainSelection === +networkId
                        // Determine if this is the initially selected chain (but not the temporarily selected one)
                        const isInitiallySelected = +networkId === +selectedChain && !isTemporarilySelected

                        return (
                            <button
                                key={`network-${isMobile ? 'mobile-' : ''}${networkId}`}
                                onClick={() => onChainClicked(+networkId)}
                                disabled={disabledChains?.includes(+networkId) && +networkId !== +selectedChain}
                                className={cn(
                                    isMobile
                                        ? 'flex flex-col items-center justify-center gap-0.5 rounded-[10px] px-1 py-1.5 transition-colors duration-200'
                                        : 'flex w-full items-center gap-2 rounded-[10px] px-3 py-2 transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-50',
                                    // Apply white background to initially selected chain if no temporary selection
                                    !activeChainSelection && isInitiallySelected
                                        ? 'border-token-modal-border bg-white text-black'
                                        : isMobile
                                          ? 'hover:bg-token-modal-chain-hover'
                                          : // Apply white background to temporarily selected chain
                                            isTemporarilySelected
                                            ? 'bg-white text-black'
                                            : // Apply a subtle indicator for the initially selected chain when another is temporarily selected
                                              isInitiallySelected
                                              ? 'text-white'
                                              : 'hover:bg-token-modal-chain-hover',
                                    // Add blue border for active search on a specific chain
                                    !isMobile &&
                                        tokenQuery &&
                                        activeChainSelection === +networkId &&
                                        'border-token-modal-accent-blue'
                                )}
                                type='button'
                            >
                                <img
                                    src={chain.icon}
                                    alt={chain.name}
                                    className={isMobile ? 'h-6 w-6 rounded-full' : 'm-0.5 h-7 w-7 rounded-full'}
                                />
                                <span
                                    className={
                                        isMobile
                                            ? 'w-full truncate text-center text-[10px] font-medium'
                                            : 'text-[14px] font-medium'
                                    }
                                >
                                    {chain.name}
                                </span>
                            </button>
                        )
                    })
            )}
        </>
    )
}

export default ChainSelector
